// Routing shim wrapping react-router-dom v7 with the Next.js navigation API
// surface used throughout the app. Centralized so call sites don't import
// from react-router-dom directly.
import { useCallback, useMemo } from 'react';
import {
  Link as RouterLink,
  useLocation,
  useNavigate,
  useParams as useRouterParams,
  useSearchParams as useRouterSearchParams,
} from 'react-router-dom';

export { useRouterParams as useParams };

export function usePathname(): string {
  return useLocation().pathname;
}

type PushOptions = { scroll?: boolean };

export function useRouter() {
  const navigate = useNavigate();
  return useMemo(() => ({
    push: (href: string, _opts?: PushOptions) => {
      void _opts;
      navigate(href);
    },
    replace: (href: string, _opts?: PushOptions) => {
      void _opts;
      navigate(href, { replace: true });
    },
    back: () => navigate(-1),
    forward: () => navigate(1),
    refresh: () => navigate(0),
    prefetch: (_href: string) => {
      void _href;
    },
  }), [navigate]);
}

// Read-only shim mirroring the Next.js useSearchParams surface.
export function useSearchParams(): URLSearchParams {
  const [params] = useRouterSearchParams();
  return params;
}

export function redirect(href: string): never {
  if (typeof window !== 'undefined') {
    window.location.replace(href);
  }
  throw new Error(`redirect: ${href}`);
}

export function notFound(): never {
  throw new Error('notFound');
}

// Convenience hook that mirrors the Next.js Link click handler so call sites
// using <a> with router.push keep working.
export function useLinkHandler() {
  const navigate = useNavigate();
  return useCallback((href: string) => () => navigate(href), [navigate]);
}

export { RouterLink };
